// Workshop Utility Functions

// Workshop Status Helpers
export const getWorkshopStatus = (workshop) => {
  if (workshop.status === "cancelled") return "cancelled";
  if (workshop.status === "draft") return "draft";

  // Check if duration exists
  if (!workshop.duration?.startDate || !workshop.duration?.endDate) {
    return "draft"; // Treat workshops without duration as drafts
  }

  const now = new Date();
  const startDate = new Date(workshop.duration.startDate);
  const endDate = new Date(workshop.duration.endDate);

  // Add some buffer time for better status transitions
  const bufferMinutes = 30;
  const startWithBuffer = new Date(
    startDate.getTime() - bufferMinutes * 60 * 1000
  );
  const endWithBuffer = new Date(endDate.getTime() + bufferMinutes * 60 * 1000);

  if (now < startWithBuffer) return "upcoming";
  if (now >= startWithBuffer && now <= endWithBuffer) return "ongoing";
  if (now > endWithBuffer) return "completed";

  return "unknown";
};

export const canUserJoin = (workshop, user) => {
  if (!user) return { canJoin: false, reason: "Authentication required" };

  // Check if workshop is in draft status
  if (workshop.status === "draft") {
    return { canJoin: false, reason: "Workshop is not yet published" };
  }

  const status = getWorkshopStatus(workshop);
  if (status !== "upcoming") {
    return { canJoin: false, reason: "Workshop is not accepting applications" };
  }

  // Check if user is already a participant
  if (workshop.userIsParticipant) {
    return { canJoin: false, reason: "Already a participant" };
  }

  // Check capacity with proper null checks
  const capacity = workshop.capacity || {};
  const isUnlimited = capacity.unlimited === true;
  const available = capacity.available || 0;

  if (!isUnlimited && available <= 0) {
    return { canJoin: false, reason: "Workshop is full" };
  }

  return { canJoin: true };
};

// Date Formatting
export const formatWorkshopDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getWorkshopDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return "1 day";
  return `${diffDays} days`;
};

export const formatDateForInput = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16); // Format for datetime-local input
};

export const isWorkshopUpcoming = (workshop) => {
  return getWorkshopStatus(workshop) === "upcoming";
};

export const isWorkshopOngoing = (workshop) => {
  return getWorkshopStatus(workshop) === "ongoing";
};

export const isWorkshopCompleted = (workshop) => {
  return getWorkshopStatus(workshop) === "completed";
};

// ========================================
// DATE HELPERS (for WorkshopCard)
// ========================================

/**
 * Get days until a specific date
 * @param {string|Date} date - Target date
 * @returns {number} Days until date (0 if past)
 */
export const getDaysUntil = (date) => {
  const target = new Date(date);
  const now = new Date();
  const diffTime = target - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

/**
 * Format date range for display
 * @example formatDateRange('2025-03-15', '2025-03-20') => "Mar 15 - Mar 20"
 */
export const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString("en-US", { month: "short" });
  const endDay = end.getDate();

  // Same month
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }

  // Different months
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

// File Upload Helpers
export const validateFile = (file, maxSize, allowedTypes) => {
  const errors = [];

  if (file.size > maxSize) {
    errors.push(`File size exceeds ${maxSize / 1024 / 1024}MB limit`);
  }

  if (!allowedTypes.includes(file.type)) {
    errors.push(`File type ${file.type} is not allowed`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const createFilePreview = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.readAsDataURL(file);
  });
};

export const validateFileUpload = (file, type = "image") => {
  const allowedTypes = {
    image: ["image/jpeg", "image/png", "image/webp"],
    paymentProof: ["image/jpeg", "image/png", "application/pdf"],
    document: ["application/pdf", "application/msword"],
  };

  const maxSizes = {
    image: 3 * 1024 * 1024, // 3MB
    paymentProof: 5 * 1024 * 1024, // 5MB
    document: 20 * 1024 * 1024, // 20MB
  };

  if (!allowedTypes[type].includes(file.type)) {
    throw new Error(
      `Invalid file type. Allowed: ${allowedTypes[type].join(", ")}`
    );
  }

  if (file.size > maxSizes[type]) {
    throw new Error(
      `File too large. Max size: ${maxSizes[type] / 1024 / 1024}MB`
    );
  }

  return true;
};

// Form Validation
export const validateWorkshopForm = (formData, schedule = []) => {
  const errors = {};

  // Title validation
  if (!formData.title || formData.title.trim().length < 5) {
    errors.title = "Title must be at least 5 characters";
  } else if (formData.title.trim().length > 200) {
    errors.title = "Title cannot exceed 200 characters";
  }

  // Description validation
  if (!formData.description || formData.description.trim().length < 20) {
    errors.description = "Description must be at least 20 characters";
  } else if (formData.description.trim().length > 2000) {
    errors.description = "Description cannot exceed 2000 characters";
  }

  // Category validation
  if (!formData.category || formData.category.trim() === "") {
    errors.category = "Category is required";
  }

  // Field validation
  if (!formData.field || formData.field.trim() === "") {
    errors.field = "Research field is required";
  }

  // Date validation
  if (!formData.duration.startDate) {
    errors.startDate = "Start date is required";
  } else {
    const startDate = new Date(formData.duration.startDate);
    const now = new Date();
    now.setMinutes(now.getMinutes() - 30); // Allow 30 minutes buffer

    if (startDate <= now) {
      errors.startDate = "Start date must be in the future";
    }
  }

  if (!formData.duration.endDate) {
    errors.endDate = "End date is required";
  } else if (formData.duration.startDate) {
    const startDate = new Date(formData.duration.startDate);
    const endDate = new Date(formData.duration.endDate);

    if (endDate <= startDate) {
      errors.endDate = "End date must be after start date";
    }
  }

  // Location validation based on format
  if (formData.format === "online") {
    if (
      !formData.location.meetingLink ||
      formData.location.meetingLink.trim() === ""
    ) {
      errors.meetingLink = "Meeting link is required for online workshops";
    } else {
      // Basic URL validation
      try {
        new URL(formData.location.meetingLink.trim());
      } catch {
        errors.meetingLink = "Please enter a valid meeting link URL";
      }
    }
  } else if (formData.format === "physical") {
    if (!formData.location.venue || formData.location.venue.trim() === "") {
      errors.venue = "Venue is required for physical workshops";
    }
    if (!formData.location.address || formData.location.address.trim() === "") {
      errors.address = "Address is required for physical workshops";
    }
  }

  // Capacity validation
  if (!formData.capacity.unlimited) {
    if (!formData.capacity.limit || formData.capacity.limit < 1) {
      errors.capacity = "Capacity limit must be at least 1";
    } else if (formData.capacity.limit > 1000) {
      errors.capacity = "Capacity limit cannot exceed 1000";
    }
  }

  // Pricing validation
  if (formData.pricing.type === "paid") {
    if (!formData.pricing.amount || formData.pricing.amount <= 0) {
      errors.amount = "Amount is required for paid workshops";
    } else if (formData.pricing.amount > 10000) {
      errors.amount = "Amount cannot exceed 10,000";
    }

    // Payment instructions validation for paid workshops
    const paymentValidation = validatePaymentInstructions(
      formData.paymentInstructions
    );
    if (!paymentValidation.isValid) {
      Object.assign(errors, paymentValidation.errors);
    }
  }

  // Duration validation
  if (!formData.duration.days || formData.duration.days < 1) {
    errors.days = "Duration must be at least 1 day";
  } else if (formData.duration.days > 30) {
    errors.days = "Duration cannot exceed 30 days";
  }

  // Date-duration consistency validation
  if (formData.duration.startDate && formData.duration.endDate) {
    const startDate = new Date(formData.duration.startDate);
    const endDate = new Date(formData.duration.endDate);

    // Reset time to compare only dates
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = Math.abs(endDate - startDate);
    const actualDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const formDays = parseInt(formData.duration.days) || 1;

    if (actualDays !== formDays) {
      errors.duration = `Duration mismatch: You selected ${formDays} day(s) but the date range spans ${actualDays} day(s). Please adjust either the duration or the dates to match.`;
    }
  }

  // Schedule validation
  if (schedule && schedule.length > 0) {
    const scheduleErrors = validateSchedule(schedule);
    if (scheduleErrors.length > 0) {
      errors.schedule = scheduleErrors;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// NEW: Comprehensive Schedule Validation
export const validateSchedule = (schedule) => {
  const errors = [];

  if (!schedule || schedule.length === 0) {
    return errors;
  }

  schedule.forEach((day, dayIndex) => {
    const dayLabel = `Day ${day.day}`;

    if (!day.sessions || day.sessions.length === 0) {
      errors.push(`${dayLabel}: At least one session is required`);
      return;
    }

    // Validate each session
    day.sessions.forEach((session, sessionIndex) => {
      const sessionLabel = `${dayLabel}, Session ${sessionIndex + 1}`;

      // Check for empty times
      if (!session.startTime || session.startTime.trim() === "") {
        errors.push(`${sessionLabel}: Start time is required`);
      }

      if (!session.endTime || session.endTime.trim() === "") {
        errors.push(`${sessionLabel}: End time is required`);
      }

      // Check for session topic
      if (!session.topic || session.topic.trim() === "") {
        errors.push(`${sessionLabel}: Session topic is required`);
      } else if (session.topic.trim().length < 3) {
        errors.push(
          `${sessionLabel}: Session topic must be at least 3 characters`
        );
      }

      // Validate time logic
      if (session.startTime && session.endTime) {
        const startTime = convertTimeToMinutes(session.startTime);
        const endTime = convertTimeToMinutes(session.endTime);

        if (startTime >= endTime) {
          errors.push(`${sessionLabel}: End time must be after start time`);
        }

        if (endTime - startTime < 30) {
          errors.push(
            `${sessionLabel}: Session must be at least 30 minutes long`
          );
        }

        if (endTime - startTime > 480) {
          // 8 hours
          errors.push(`${sessionLabel}: Session cannot exceed 8 hours`);
        }
      }
    });

    // Check for overlapping sessions within the same day
    const overlaps = findOverlappingSessions(day.sessions);
    overlaps.forEach((overlap) => {
      errors.push(
        `${dayLabel}: Sessions ${overlap.session1 + 1} and ${
          overlap.session2 + 1
        } have overlapping times`
      );
    });

    // Check for reasonable day duration (max 12 hours)
    const dayDuration = calculateDayDuration(day.sessions);
    if (dayDuration > 720) {
      // 12 hours
      errors.push(`${dayLabel}: Total day duration cannot exceed 12 hours`);
    }
  });

  return errors;
};

// Helper function to convert time string to minutes
export const convertTimeToMinutes = (timeString) => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

// Helper function to find overlapping sessions
export const findOverlappingSessions = (sessions) => {
  const overlaps = [];

  for (let i = 0; i < sessions.length; i++) {
    for (let j = i + 1; j < sessions.length; j++) {
      const session1 = sessions[i];
      const session2 = sessions[j];

      if (
        !session1.startTime ||
        !session1.endTime ||
        !session2.startTime ||
        !session2.endTime
      ) {
        continue;
      }

      const start1 = convertTimeToMinutes(session1.startTime);
      const end1 = convertTimeToMinutes(session1.endTime);
      const start2 = convertTimeToMinutes(session2.startTime);
      const end2 = convertTimeToMinutes(session2.endTime);

      // Check for overlap
      if (start1 < end2 && start2 < end1) {
        overlaps.push({ session1: i, session2: j });
      }
    }
  }

  return overlaps;
};

// Helper function to calculate total day duration
export const calculateDayDuration = (sessions) => {
  if (!sessions || sessions.length === 0) return 0;

  let earliestStart = Infinity;
  let latestEnd = 0;

  sessions.forEach((session) => {
    if (session.startTime && session.endTime) {
      const start = convertTimeToMinutes(session.startTime);
      const end = convertTimeToMinutes(session.endTime);

      earliestStart = Math.min(earliestStart, start);
      latestEnd = Math.max(latestEnd, end);
    }
  });

  return earliestStart === Infinity ? 0 : latestEnd - earliestStart;
};

// NEW: Validate individual session
export const validateSession = (session, sessionIndex = 0, dayIndex = 0) => {
  const errors = [];
  const sessionLabel = `Day ${dayIndex + 1}, Session ${sessionIndex + 1}`;

  if (!session.startTime || session.startTime.trim() === "") {
    errors.push(`${sessionLabel}: Start time is required`);
  }

  if (!session.endTime || session.endTime.trim() === "") {
    errors.push(`${sessionLabel}: End time is required`);
  }

  if (!session.topic || session.topic.trim() === "") {
    errors.push(`${sessionLabel}: Session topic is required`);
  }

  if (session.startTime && session.endTime) {
    const startTime = convertTimeToMinutes(session.startTime);
    const endTime = convertTimeToMinutes(session.endTime);

    if (startTime >= endTime) {
      errors.push(`${sessionLabel}: End time must be after start time`);
    }

    if (endTime - startTime < 30) {
      errors.push(`${sessionLabel}: Session must be at least 30 minutes long`);
    }
  }

  return errors;
};

// NEW: Payment Instructions Validation
export const validatePaymentInstructions = (paymentInstructions) => {
  const errors = {};
  let hasValidPaymentMethod = false;

  if (!paymentInstructions) {
    return {
      isValid: false,
      errors: {
        payment:
          "At least one payment method must be provided for paid workshops",
      },
    };
  }

  // Check Bank Details
  const bankDetails = paymentInstructions.bankDetails || {};
  if (bankDetails.accountNumber && bankDetails.accountNumber.trim()) {
    // Only count as valid if it passes validation
    if (
      bankDetails.accountNumber.trim().length <= 50 &&
      /^[0-9-]+$/.test(bankDetails.accountNumber.trim())
    ) {
      hasValidPaymentMethod = true;
    }

    // Validate account number format
    if (!/^[0-9-]+$/.test(bankDetails.accountNumber.trim())) {
      errors.bankAccountNumber =
        "Account number should only contain numbers and hyphens";
    }
    if (bankDetails.accountNumber.trim().length > 50) {
      errors.bankAccountNumber = "Account number cannot exceed 50 characters";
    }

    // Validate account title if provided (FIXED: Allow numbers and & symbol)
    if (bankDetails.accountTitle && bankDetails.accountTitle.trim()) {
      if (!/^[a-zA-Z0-9\s\-_.,&]+$/.test(bankDetails.accountTitle.trim())) {
        errors.bankAccountTitle =
          "Account title contains invalid characters. Use only letters, numbers, spaces, hyphens, underscores, periods, commas, and ampersands";
      }
      if (bankDetails.accountTitle.trim().length > 100) {
        errors.bankAccountTitle = "Account title cannot exceed 100 characters";
      }
    }

    // Validate bank name if provided (FIXED: Allow numbers and & symbol)
    if (bankDetails.bankName && bankDetails.bankName.trim()) {
      if (!/^[a-zA-Z0-9\s\-_.,&]+$/.test(bankDetails.bankName.trim())) {
        errors.bankName =
          "Bank name contains invalid characters. Use only letters, numbers, spaces, hyphens, underscores, periods, commas, and ampersands";
      }
      if (bankDetails.bankName.trim().length > 100) {
        errors.bankName = "Bank name cannot exceed 100 characters";
      }
    }

    // Validate IBAN if provided
    if (bankDetails.iban && bankDetails.iban.trim()) {
      if (!/^[A-Z0-9]+$/.test(bankDetails.iban.trim())) {
        errors.bankIban =
          "IBAN should only contain uppercase letters and numbers";
      }
      if (bankDetails.iban.trim().length > 50) {
        errors.bankIban = "IBAN cannot exceed 50 characters";
      }
    }
  }

  // Check EasyPaisa
  const easypaisa = paymentInstructions.mobilePayment?.easypaisa || {};
  if (easypaisa.accountNumber && easypaisa.accountNumber.trim()) {
    // Only count as valid if it passes validation
    if (
      easypaisa.accountNumber.trim().length <= 20 &&
      /^(\+92|0)?3[0-9]{9}$/.test(easypaisa.accountNumber.trim())
    ) {
      hasValidPaymentMethod = true;
    }

    // ✅ FIXED: International phone validation (EasyPaisa is Pakistan-specific but allow international format)
    if (easypaisa.accountNumber.trim().length > 20) {
      errors.easypaisaNumber = "EasyPaisa number cannot exceed 20 characters";
    } else if (!/^(\+92|0)?3[0-9]{9}$/.test(easypaisa.accountNumber.trim())) {
      errors.easypaisaNumber =
        "Please enter a valid Pakistan mobile number (e.g., 03001234567 or +923001234567)";
    }

    // Validate account title if provided
    if (easypaisa.accountTitle && easypaisa.accountTitle.trim()) {
      if (!/^[a-zA-Z\s\-_.,]+$/.test(easypaisa.accountTitle.trim())) {
        errors.easypaisaTitle =
          "Account title should only contain letters and basic punctuation";
      }
      if (easypaisa.accountTitle.trim().length > 100) {
        errors.easypaisaTitle = "Account title cannot exceed 100 characters";
      }
    }
  }

  // Check JazzCash
  const jazzcash = paymentInstructions.mobilePayment?.jazzcash || {};
  if (jazzcash.accountNumber && jazzcash.accountNumber.trim()) {
    // Only count as valid if it passes validation
    if (
      jazzcash.accountNumber.trim().length <= 20 &&
      /^(\+92|0)?3[0-9]{9}$/.test(jazzcash.accountNumber.trim())
    ) {
      hasValidPaymentMethod = true;
    }

    // ✅ FIXED: International phone validation (JazzCash is Pakistan-specific but allow international format)
    if (jazzcash.accountNumber.trim().length > 20) {
      errors.jazzcashNumber = "JazzCash number cannot exceed 20 characters";
    } else if (!/^(\+92|0)?3[0-9]{9}$/.test(jazzcash.accountNumber.trim())) {
      errors.jazzcashNumber =
        "Please enter a valid Pakistan mobile number (e.g., 03001234567 or +923001234567)";
    }

    // Validate account title if provided
    if (jazzcash.accountTitle && jazzcash.accountTitle.trim()) {
      if (!/^[a-zA-Z\s\-_.,]+$/.test(jazzcash.accountTitle.trim())) {
        errors.jazzcashTitle =
          "Account title should only contain letters and basic punctuation";
      }
      if (jazzcash.accountTitle.trim().length > 100) {
        errors.jazzcashTitle = "Account title cannot exceed 100 characters";
      }
    }
  }

  // Check PayPal
  const paypal = paymentInstructions.internationalPayment?.paypal || {};
  if (paypal.email && paypal.email.trim()) {
    // Only count as valid if it passes validation
    if (
      paypal.email.trim().length <= 100 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypal.email.trim())
    ) {
      hasValidPaymentMethod = true;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(paypal.email.trim())) {
      errors.paypalEmail = "Please enter a valid email address";
    }
    if (paypal.email.trim().length > 100) {
      errors.paypalEmail = "Email cannot exceed 100 characters";
    }
  }

  // Check Wise
  const wise = paymentInstructions.internationalPayment?.wise || {};
  if (wise.email && wise.email.trim()) {
    // Only count as valid if it passes validation
    if (
      wise.email.trim().length <= 100 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(wise.email.trim())
    ) {
      hasValidPaymentMethod = true;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(wise.email.trim())) {
      errors.wiseEmail = "Please enter a valid email address";
    }
    if (wise.email.trim().length > 100) {
      errors.wiseEmail = "Email cannot exceed 100 characters";
    }
  }

  // Check if at least one payment method is provided
  if (!hasValidPaymentMethod) {
    errors.payment =
      "At least one payment method must be provided for paid workshops";
  }

  // Validate additional instructions if provided
  if (
    paymentInstructions.additionalInstructions &&
    paymentInstructions.additionalInstructions.trim()
  ) {
    if (paymentInstructions.additionalInstructions.trim().length > 1000) {
      errors.additionalInstructions =
        "Additional instructions cannot exceed 1000 characters";
    }
  }

  // Validate contact info if provided
  const contactInfo = paymentInstructions.contactInfo || {};
  if (contactInfo.whatsapp && contactInfo.whatsapp.trim()) {
    const whatsappNumber = contactInfo.whatsapp.trim();

    // ✅ FIXED: International WhatsApp validation - support all countries
    if (whatsappNumber.length > 20) {
      errors.contactWhatsapp = "WhatsApp number cannot exceed 20 characters";
    } else if (whatsappNumber.length < 7) {
      errors.contactWhatsapp = "WhatsApp number is too short";
    } else if (!/^(\+?[1-9]\d{6,19}|0[1-9]\d{6,18})$/.test(whatsappNumber)) {
      errors.contactWhatsapp =
        "Please enter a valid WhatsApp number (e.g., +1234567890, +923001234567, or 03001234567)";
    }
  }

  if (contactInfo.email && contactInfo.email.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactInfo.email.trim())) {
      errors.contactEmail = "Please enter a valid email address";
    }
    if (contactInfo.email.trim().length > 100) {
      errors.contactEmail = "Email cannot exceed 100 characters";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0 && hasValidPaymentMethod,
    errors,
    hasValidPaymentMethod,
  };
};

// Helper function to check if a payment method is complete
export const isPaymentMethodComplete = (paymentInstructions, method) => {
  if (!paymentInstructions) return false;

  switch (method) {
    case "bank":
      return !!paymentInstructions.bankDetails?.accountNumber?.trim();
    case "easypaisa":
      return !!paymentInstructions.mobilePayment?.easypaisa?.accountNumber?.trim();
    case "jazzcash":
      return !!paymentInstructions.mobilePayment?.jazzcash?.accountNumber?.trim();
    case "paypal":
      return !!paymentInstructions.internationalPayment?.paypal?.email?.trim();
    case "wise":
      return !!paymentInstructions.internationalPayment?.wise?.email?.trim();
    default:
      return false;
  }
};

// Helper function to get payment method completion status
export const getPaymentMethodsStatus = (paymentInstructions) => {
  return {
    bank: isPaymentMethodComplete(paymentInstructions, "bank"),
    easypaisa: isPaymentMethodComplete(paymentInstructions, "easypaisa"),
    jazzcash: isPaymentMethodComplete(paymentInstructions, "jazzcash"),
    paypal: isPaymentMethodComplete(paymentInstructions, "paypal"),
    wise: isPaymentMethodComplete(paymentInstructions, "wise"),
  };
};

// Pricing Helpers
export const formatPrice = (amount, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

export const isPaidWorkshop = (workshop) => {
  return workshop.pricing.type === "paid";
};

export const isFreeWorkshop = (workshop) => {
  return workshop.pricing.type === "free";
};

// Participant Status Helpers
export const getParticipantStatusText = (status) => {
  const statusMap = {
    pending: "Pending Approval",
    approved: "Enrolled",
    rejected: "Application Rejected",
    payment_pending: "Payment Pending",
  };
  return statusMap[status] || "Unknown Status";
};

export const getParticipantStatusColor = (status) => {
  const colorMap = {
    pending: "#ff9800", // Orange
    approved: "#00c853", // Green (theme color)
    rejected: "#f44336", // Red
    payment_pending: "#2196f3", // Blue
  };
  return colorMap[status] || "#757575"; // Gray for unknown
};

// Workshop Category Helpers
export const getPopularCategories = () => {
  return [
    "Machine Learning",
    "Data Science",
    "Web Development",
    "Mobile Development",
    "Artificial Intelligence",
    "Blockchain",
    "Cybersecurity",
    "UI/UX Design",
    "Digital Marketing",
    "Project Management",
  ];
};

export const getResearchFields = () => {
  return [
    "Computer Science",
    "Data Science",
    "Engineering",
    "Mathematics",
    "Physics",
    "Biology",
    "Chemistry",
    "Medicine",
    "Psychology",
    "Business",
    "Economics",
    "Social Sciences",
  ];
};

// Search and Filter Helpers
export const createSearchFilters = (searchTerm, filters) => {
  const searchFilters = {};

  if (searchTerm) {
    searchFilters.query = searchTerm;
  }

  if (filters.category && filters.category !== "all") {
    searchFilters.category = filters.category;
  }

  if (filters.field && filters.field !== "all") {
    searchFilters.field = filters.field;
  }

  if (filters.format && filters.format !== "all") {
    searchFilters.format = filters.format;
  }

  if (filters.pricing && filters.pricing !== "all") {
    searchFilters.pricing = filters.pricing;
  }

  if (filters.status && filters.status !== "all") {
    searchFilters.status = filters.status;
  }

  return searchFilters;
};

// URL Helpers
export const generateWorkshopSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Time Helpers
export const getTimeUntilWorkshop = (startDate) => {
  const now = new Date();
  const start = new Date(startDate);
  const diffTime = start - now;

  if (diffTime <= 0) return "Starting Soon";

  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (days > 7) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} away`;
  } else if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} away`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} away`;
  } else {
    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    if (minutes > 5) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} away`;
    } else {
      return "Starting Soon";
    }
  }
};

// Error Handling
export const handleApiError = (error, showNotification) => {
  let message = "Something went wrong. Please try again.";

  if (error.message) {
    message = error.message;
  }

  if (showNotification) {
    showNotification(message, "error");
  }

  return message;
};

// Local Storage Helpers
export const saveWorkshopDraft = (formData) => {
  localStorage.setItem("workshopDraft", JSON.stringify(formData));
};

export const getWorkshopDraft = () => {
  const draft = localStorage.getItem("workshopDraft");
  return draft ? JSON.parse(draft) : null;
};

export const clearWorkshopDraft = () => {
  localStorage.removeItem("workshopDraft");
};

// Export all utilities as default
const workshopUtils = {
  getWorkshopStatus,
  canUserJoin,
  formatWorkshopDate,
  getWorkshopDuration,
  formatDateForInput,
  isWorkshopUpcoming,
  isWorkshopOngoing,
  isWorkshopCompleted,
  validateFile,
  createFilePreview,
  validateFileUpload,
  validateWorkshopForm,
  validatePaymentInstructions,
  isPaymentMethodComplete,
  getPaymentMethodsStatus,
  formatPrice,
  isPaidWorkshop,
  isFreeWorkshop,
  getParticipantStatusText,
  getParticipantStatusColor,
  getPopularCategories,
  getResearchFields,
  createSearchFilters,
  generateWorkshopSlug,
  getTimeUntilWorkshop,
  handleApiError,
  saveWorkshopDraft,
  getWorkshopDraft,
  clearWorkshopDraft,
};

export default workshopUtils;
